"use client";

import { FormEvent, useId, useState } from "react";
import { emailRegExp } from "../utils/emailValidationRegex";
import styles from "./HomeSubscribe.module.css";

type Status = "idle" | "loading" | "success";
type Variant = "dark" | "light";

type HomeSubscribeProps = {
  id?: string;
  variant?: Variant;
  stacked?: boolean;
};

function getFormClass(variant: Variant, stacked: boolean): string {
  switch (variant) {
    case "dark":
      return stacked
        ? `${styles.form} ${styles.dark} ${styles.stacked}`
        : `${styles.form} ${styles.dark}`;
    case "light":
      return stacked
        ? `${styles.form} ${styles.light} ${styles.stacked}`
        : `${styles.form} ${styles.light}`;
    default: {
      const _never: never = variant;
      return _never;
    }
  }
}

export function HomeSubscribe({
  id,
  variant = "dark",
  stacked = false,
}: HomeSubscribeProps) {
  const generatedId = useId();
  const formId = id ?? generatedId;
  const emailFieldId = `${formId}-email`;
  const errorId = `${formId}-error`;

  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === "loading") return;

    emailRegExp.lastIndex = 0;
    if (!emailRegExp.test(email.trim())) {
      setError("Enter a valid email address.");
      return;
    }

    setError(null);
    setStatus("loading");

    try {
      const response = await fetch("/api/addSubscriber", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });

      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as
          | { error?: string }
          | null;
        setError(payload?.error ?? "Something went wrong. Try again in a moment.");
        setStatus("idle");
        return;
      }

      setEmail("");
      setStatus("success");
    } catch {
      setError("Something went wrong. Try again in a moment.");
      setStatus("idle");
    }
  }

  if (status === "success") {
    return (
      <div className={styles.wrap}>
        <p className={`${styles.success} ${styles[variant]}`} role="status">
          You&apos;re in. Check your inbox, and spam, just in case.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <form
        id={formId}
        className={getFormClass(variant, stacked)}
        onSubmit={onSubmit}
        noValidate
        aria-busy={status === "loading"}
      >
        <label htmlFor={emailFieldId} className={styles.srOnly}>
          Email address
        </label>
        <input
          id={emailFieldId}
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="Email address"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            if (error) setError(null);
          }}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          disabled={status === "loading"}
        />
        <button type="submit" disabled={!email.trim() || status === "loading"}>
          {status === "loading" ? "Subscribing" : "Subscribe"}
        </button>
      </form>
      {error ? (
        <p id={errorId} className={`${styles.error} ${styles[variant]}`} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
